import { json } from "@codemirror/lang-json";
import { Prec, Range, StateField } from "@codemirror/state";
import { Decoration, DecorationSet, EditorView, keymap, ViewPlugin, ViewUpdate, WidgetType } from "@codemirror/view";
import { vscodeDark, vscodeLight } from "@uiw/codemirror-theme-vscode";
import CodeMirror from "@uiw/react-codemirror";
import React, { useMemo } from "react";
import { useTheme } from "src/context/ThemeContext";

// Custom keymap for handling Enter key within JSON strings
const jsonStringKeymap = (() => {
  return Prec.high(keymap.of([
    {
      key: "Enter",
      run: (view: EditorView) => {
        const { state } = view;
        const { selection } = state;

        // Only handle single cursor selections
        if (selection.ranges.length !== 1) {
          return false;
        }

        const cursor = selection.main.head;
        const doc = state.doc;

        // Get the text from the beginning of the document to the cursor
        const textBeforeCursor = doc.sliceString(0, cursor);

        // Function to check if cursor is inside a JSON string
        const isInsideString = (text: string): boolean => {
          let inString = false;
          let i = 0;

          while (i < text.length) {
            const char = text[i];

            if (char === '"') {
              // Check if this quote is escaped
              let backslashCount = 0;
              let j = i - 1;
              while (j >= 0 && text[j] === "\\") {
                backslashCount++;
                j--;
              }

              // If even number of backslashes (including 0), the quote is not escaped
              if (backslashCount % 2 === 0) {
                inString = !inString;
              }
            }
            i++;
          }

          return inString;
        };

        // Check if cursor is inside a string
        if (isInsideString(textBeforeCursor)) {
          view.dispatch({
            changes: {
              from: cursor,
              insert: "\\n"
            },
            selection: { anchor: cursor + 2 }
          });
          return true;
        }

        // Let default behavior handle other cases
        return false;
      }
    }
  ]));
})();

// Widget class for visual line breaks
class LineBreakWidget extends WidgetType {
  toDOM() {
    const span = document.createElement("span");
    span.style.display = "block";
    span.style.height = "0";
    span.style.margin = "2px 0";

    return span;
  }

  eq(other: any) {
    return other instanceof LineBreakWidget;
  }

  updateDOM() {
    return false;
  }

  get estimatedHeight() {
    return 4;
  }

  ignoreEvent() {
    return false;
  }
}

// Visual rendering plugin for \n sequences in JSON strings
const jsonStringLineBreakPlugin = ViewPlugin.fromClass(class {
  decorations: DecorationSet;

  constructor(view: EditorView) {
    this.decorations = this.buildDecorations(view);
  }

  update(update: ViewUpdate) {
    if (update.docChanged || update.viewportChanged) {
      this.decorations = this.buildDecorations(update.view);
    }
  }

  buildDecorations(view: EditorView): DecorationSet {
    const decorations: Range<Decoration>[] = [];
    const doc = view.state.doc;
    const text = doc.toString();

    // Find all JSON strings and their \n sequences
    let inString = false;
    let stringStart = 0;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];

      if (char === '"') {
        // Check if this quote is escaped
        let backslashCount = 0;
        let j = i - 1;
        while (j >= 0 && text[j] === "\\") {
          backslashCount++;
          j--;
        }

        // If even number of backslashes (including 0), the quote is not escaped
        if (backslashCount % 2 === 0) {
          if (!inString) {
            inString = true;
            stringStart = i;
          } else {
            // End of string - process \n sequences in this string
            const stringContent = text.substring(stringStart + 1, i);
            this.processStringContent(stringContent, stringStart + 1, decorations);
            inString = false;
          }
        }
      }
    }

    return Decoration.set(decorations);
  }

  processStringContent(content: string, startPos: number, decorations: Range<Decoration>[]) {
    let pos = 0;
    while (pos < content.length) {
      const index = content.indexOf("\\n", pos);
      if (index === -1) break;

      // Create a decoration that replaces \n with a visual line break
      const from = startPos + index;
      const to = startPos + index + 2;

      decorations.push({
        from,
        to,
        value: Decoration.replace({
          widget: new LineBreakWidget()
        })
      });

      pos = index + 2;
    }
  }
}, {
  decorations: v => v.decorations
});

interface JSONEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  height?: string;
  error?: boolean;
  errorLine?: number;
  className?: string;
  readOnly?: boolean;
}

// Error line highlighting
const createErrorLineExtension = (errorLine: number | null) => {
  return StateField.define<DecorationSet>({
    create(state) {
      const decorations: Range<Decoration>[] = [];
      if (errorLine !== null && errorLine > 0) {
        const line = state.doc.line(Math.min(errorLine, state.doc.lines));
        const lineDecoration = Decoration.line({
          attributes: {
            class: "cm-error-line",
            style: "background-color: rgba(239, 68, 68, 0.15); border-left: 3px solid #ef4444;"
          }
        });
        decorations.push(lineDecoration.range(line.from));
      }
      return Decoration.set(decorations);
    },
    update(decorations) {
      return decorations;
    },
    provide: field => EditorView.decorations.from(field)
  });
};

export function JSONEditor({
  value,
  onChange,
  placeholder = "Edit JSON",
  minHeight,
  height,
  error = false,
  errorLine,
  className = "",
  readOnly = false
}: JSONEditorProps) {
  const { resolvedTheme } = useTheme();
  const editorTheme = resolvedTheme === "dark" ? vscodeDark : vscodeLight;

  const extensions = useMemo(() => {
    const baseExtensions = [
      jsonStringKeymap,
      jsonStringLineBreakPlugin,
      json(),
      EditorView.lineWrapping
    ];

    if (errorLine) {
      baseExtensions.push(createErrorLineExtension(errorLine));
    }

    if (readOnly) {
      baseExtensions.push(EditorView.editable.of(false));
    }

    return baseExtensions;
  }, [errorLine, readOnly]);

  const style = {
    fontSize: 12,
    border: error ? "1px solid #ef4444" : "1px solid hsl(var(--border))",
    borderRadius: "6px",
    backgroundColor: "transparent",
  };

  return (
    <div className={`custom-scrollbar ${className}`}>
      <CodeMirror
        value={value}
        height={height}
        minHeight={minHeight}
        theme={editorTheme}
        extensions={extensions}
        onChange={onChange}
        placeholder={placeholder}
        autoFocus={true}
        basicSetup={{
          lineNumbers: true,
          foldGutter: true,
          dropCursor: false,
          allowMultipleSelections: false,
          indentOnInput: true,
          bracketMatching: true,
          closeBrackets: true,
          autocompletion: true,
          highlightSelectionMatches: false,
        }}
        style={style}
      />
    </div>
  );
}